import { useIntl } from 'react-intl';
import { useUpdateSamlProviderForm } from '../../../hooks/Saml/useUpdateSamlProviderForm';
import { UpdateSamlIdentityProviderDto } from '../../../../../api-client';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { UpdateSamlIdentityProviderModalComponent } from './UpdateSamlIdentityProviderModal.types';
import { Box, Button, Flexbox, Modal, Select, TextField } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { Controller, useFieldArray } from 'react-hook-form';
import { CommonMessages } from '../../../../../common';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { promptOptions } from '../../../utils/propmtOptions';
import { CertificatesList } from '../../CertificatesList/CertificatesList';
import { ExternalIdpLogoutConfigForm } from '../../IdentityProvidersList/components/ExternalIdpLogoutConfigForm/ExternalIdpLogoutConfigForm';

export const UpdateSamlIdentityProviderModal: UpdateSamlIdentityProviderModalComponent = ({
  onClose,
  isOpen,
  updateIdentityProvider,
  isUpdating,
  identityProvider,
  error,
}) => {
  const intl = useIntl();
  const [showIdpLogoutConfig, setShowIdpLogoutConfig] = useState<boolean>(false);
  const { control, handleSubmit, formState, reset, trigger, setValue } =
    useUpdateSamlProviderForm();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'certificates',
  });

  const handleAddIdpLogoutConfig = useCallback(() => {
    setShowIdpLogoutConfig((value) => !value);
    if (!showIdpLogoutConfig) {
      setValue(
        'externalIdpLogoutConfig.redirectUrl',
        identityProvider.externalIdpLogoutConfig?.redirectUrl ?? '',
      );
      setValue(
        'externalIdpLogoutConfig.enabled',
        identityProvider.externalIdpLogoutConfig?.enabled ?? true,
      );
    } else {
      setValue('externalIdpLogoutConfig', undefined);
    }
    trigger('externalIdpLogoutConfig');
  }, [
    identityProvider.externalIdpLogoutConfig?.enabled,
    identityProvider.externalIdpLogoutConfig?.redirectUrl,
    setValue,
    showIdpLogoutConfig,
    trigger,
  ]);

  const onFileLoad = (index: number) => async (event: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const file = event.target.files[0];
    const text = await file.text();
    const formattedText = text.replace(/\r/gm, '').trim();
    update(index, { value: formattedText });
    trigger(`certificates[${index}].value`);
  };

  const onFormSubmit = (
    updateIdentityProviderDto: Omit<UpdateSamlIdentityProviderDto, 'certificates'> & {
      certificates: Array<{ value: string }>;
    },
  ) => {
    updateIdentityProvider({
      id: identityProvider.id,
      updateIdentityProviderDto: {
        ...updateIdentityProviderDto,
        externalIdpLogoutConfig: updateIdentityProviderDto?.externalIdpLogoutConfig ?? undefined,
        certificates: updateIdentityProviderDto.certificates.map((cert) => cert.value),
      },
    });
  };

  useEffect(() => {
    reset({
      name: identityProvider.name,
      entityId: identityProvider.entityId,
      externalIdField: identityProvider.externalIdField,
      rpEntityId: identityProvider.rpEntityId,
      ssoUrl: identityProvider.ssoUrl,
      prompt: identityProvider.prompt,
      certificates: identityProvider.certificates,
      externalIdpLogoutConfig: identityProvider.externalIdpLogoutConfig ?? null,
    });
  }, [
    identityProvider.certificates,
    identityProvider.entityId,
    identityProvider.externalIdField,
    identityProvider.externalIdpLogoutConfig,
    identityProvider.name,
    identityProvider.prompt,
    identityProvider.rpEntityId,
    identityProvider.ssoUrl,
    isOpen,
    reset,
    setValue,
  ]);

  useEffect(() => {
    setShowIdpLogoutConfig(!!identityProvider.externalIdpLogoutConfig);
  }, [identityProvider.externalIdpLogoutConfig, isOpen]);

  return isOpen ? (
    <Modal
      title={`Update ${identityProvider.name}`}
      data-testid="update-identity-provider-modal"
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            disabled={
              !formState.isValid || Object.keys(formState?.dirtyFields).length < 1 || isUpdating
            }
            //@ts-ignore
            onClick={handleSubmit(onFormSubmit)}
          >
            {isUpdating ? 'Updating ...' : 'Update'}
          </Button>
        </Flexbox>
      }
      onClose={onClose}
    >
      <ErrorBox error={error} />
      <form>
        <Box mb={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                fullWidth
                tabIndex={0}
                label={intl.formatMessage(CommonMessages.name)}
                type="text"
                name="name"
                ref={ref}
                value={value}
                error={formState.errors?.name?.message as string | undefined}
                onChange={onChange}
                // @ts-ignore
                hint={
                  <span>
                    Unique name for the identity provider
                    <br />
                    Must be between 3 and 256 characters long
                  </span>
                }
              />
            )}
            defaultValue={identityProvider.name}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="entityId"
            defaultValue={identityProvider.entityId}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label={intl.formatMessage(CommonMessages.entityId)}
                name="entityId"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.entityId?.message as string | undefined}
                hint="Can not be longer than 512 characters"
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            control={control}
            name="prompt"
            render={({ field: { value, onChange } }) => (
              <Select
                label={intl.formatMessage(CommonMessages.prompt)}
                name="prompt"
                value={promptOptions.find((option) => option.value === value) ?? undefined}
                isClearable={false}
                isSearchable={false}
                placeholder={value}
                onChange={(option: Option | null) => onChange(option?.value)}
                error={formState.errors?.prompt?.message as string | undefined}
                options={promptOptions}
                initialValue={{ label: 'Select account', value: 'select_account' }}
                fullWidth
                hint="Specifies whether the Authorization Server prompts the End-User for reauthentication and consent"
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            defaultValue={identityProvider.externalIdField}
            name="externalIdField"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                label={intl.formatMessage(CommonMessages.externalIdField)}
                fullWidth
                name="externalIdField"
                value={value?.value}
                isClearable={false}
                isSearchable={false}
                placeholder={value}
                onChange={(option: Option | null) => onChange(option?.value)}
                options={[
                  { label: intl.formatMessage(CommonMessages.uid), value: 'Uid' },
                  { label: intl.formatMessage(CommonMessages.phoneNumber), value: 'PhoneNumber' },
                  { label: intl.formatMessage(CommonMessages.email), value: 'Email' },
                ]}
                error={formState.errors?.externalIdField?.message as string | undefined}
                // @ts-ignore
                hint={
                  <span>
                    Specifies which field will be used by Hii Retail as a{' '}
                    <strong>unique user identifier</strong>,<br /> when importing users from the 3rd
                    party identity platform
                  </span>
                }
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="ssoUrl"
            defaultValue={identityProvider.ssoUrl}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label={intl.formatMessage(CommonMessages.ssoUrl)}
                name="ssoUrl"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.ssoUrl?.message as string | undefined}
                // @ts-ignore
                hint={
                  <span>
                    Provider's single sign-on URL
                    <br />
                  </span>
                }
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <Controller
            name="rpEntityId"
            defaultValue={identityProvider.rpEntityId}
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                fullWidth
                label={intl.formatMessage(CommonMessages.rpEntityId)}
                name="rpEntityId"
                ref={ref}
                type="text"
                value={value}
                onChange={onChange}
                error={formState.errors?.rpEntityId?.message as string | undefined}
                // @ts-ignore
                hint={
                  <span>
                    The SAML relying party (RP/SP) entity ID.
                    <br />
                    This is typically your app's URL. On your SAML identity provider, this is
                    referred to as the audience
                  </span>
                }
              />
            )}
          />
        </Box>

        <ExternalIdpLogoutConfigForm
          isOpen={showIdpLogoutConfig}
          handleAddForm={handleAddIdpLogoutConfig}
          formState={formState}
          control={control}
          disableButton={showIdpLogoutConfig && !!identityProvider?.externalIdpLogoutConfig}
        />

        <CertificatesList
          fields={fields}
          control={control}
          formState={formState}
          onFileLoad={onFileLoad}
          remove={remove}
          append={append}
          identityProvider={identityProvider}
        />
      </form>
    </Modal>
  ) : null;
};
